import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { MessageComposer } from './MessageComposer';

/**
 * UserDetailDrawer
 * -----------------
 * Side drawer rendered by the Admin Users tab when an operator clicks
 * "View details" on a parent (or any user) row. Shows a 3-section
 * snapshot of the user's full footprint inside the org so the admin
 * can answer "who is this person and what's connected to them?" without
 * leaving the Users tab.
 *
 * Sections:
 *   1. Profile           — name, email, phone, role, approval status
 *   2. Children          — every child whose parent_id is this user
 *   3. Enrolments        — every enrolment record this parent has submitted
 *   4. Recent Bookings   — bookings against any of their children
 *
 * Cross-entity navigation:
 *   - Each child has an "Open child profile" link → /child/:id
 *   - Each enrolment has a "View signed PDF" link to the printable HTML.
 *
 * Props:
 *   - user      : the user object to display (must include _id)
 *   - onClose   : () => void
 */
export const UserDetailDrawer = ({ user, onClose, currentUser = null }) => {
  const [tab, setTab] = useState('profile');
  const [children, setChildren] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  // "✉️ Message" button on the drawer header — opens the shared composer
  // pre-bound to this parent. We only show it for parents and only to
  // admins (not to other parents).
  const [showComposer, setShowComposer] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Phase 10 — Photo Approval Workflow.
  // We let managers upload a profile photo on behalf of staff straight
  // from the drawer. These uploads bypass the approval queue (they're a
  // verification action by definition) and replace the live avatar
  // immediately. We mirror the latest avatar URL locally so the drawer
  // updates without re-fetching the parent list.
  const photoInputRef = React.useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState(null);

  // Roles that may upload (and auto-approve) another user's photo.
  // Mirrors the backend `_AVATAR_MANAGER_ROLES` set.
  const MANAGER_ROLES = ['admin', 'org_owner', 'org_admin', 'deputy_manager'];
  // Roles for which the photo is meaningful (i.e. staff who get an ID card).
  const STAFF_ID_ROLES = ['staff', 'room_leader', 'driver', 'deputy_manager', 'admin', 'org_owner', 'org_admin'];

  const viewerRole = (currentUser?.role || '').toLowerCase();
  const targetRole = (user?.role || '').toLowerCase();
  const canManageThisPhoto = MANAGER_ROLES.includes(viewerRole) && STAFF_ID_ROLES.includes(targetRole);
  const liveAvatarUrl = localAvatarUrl || user?.avatar_url;

  async function handleManagerPhotoSelected(e) {
    const file = e.target.files && e.target.files[0];
    try { e.target.value = ''; } catch { /* ignore */ }
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError('Photo is too large (max 8 MB).');
      return;
    }
    setPhotoBusy(true);
    setError('');
    try {
      const out = await api.uploadUserAvatar(user._id, file);
      // Manager-uploads come back already approved. Show the new image
      // immediately + a cache-buster so the <img> reloads even if the
      // URL didn't change.
      setLocalAvatarUrl(`${out.avatar_url}?v=${Date.now()}`);
    } catch (err) {
      setError(err.message || 'Could not upload photo');
    } finally {
      setPhotoBusy(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    // Reset the manager-upload preview when switching to a different user.
    setLocalAvatarUrl(null);
    Promise.all([
      api.getUserChildren(user._id).catch((e) => { console.warn('children load failed', e); return []; }),
      api.getUserEnrollments(user._id).catch((e) => { console.warn('enrollments load failed', e); return []; }),
      api.getUserBookings(user._id).catch((e) => { console.warn('bookings load failed', e); return []; }),
    ]).then(([c, e, b]) => {
      if (cancelled) return;
      setChildren(c);
      setEnrollments(e);
      setBookings(b);
    }).catch((err) => {
      if (!cancelled) setError(err.message || 'Failed to load details');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return null;

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(); } catch { return String(d).slice(0, 10); }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="user-detail-drawer"
      style={{ alignItems: 'stretch', justifyContent: 'flex-end' }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 100%)',
          height: '100vh',
          maxHeight: '100vh',
          maxWidth: '100%',
          margin: 0,
          borderRadius: 0,
          overflowY: 'auto',
          padding: '1.25rem',
        }}
      >
        {/* ---------- Header ---------- */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            marginBottom: '1rem',
            paddingBottom: '0.75rem',
            borderBottom: '1px solid hsl(var(--border))',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }} data-testid="drawer-user-name">
              {user.full_name || user.email}
            </h2>
            <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
              {user.email}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <span
                className="badge"
                style={{
                  padding: '0.15rem 0.5rem', borderRadius: 4,
                  background: 'hsl(var(--muted))', fontSize: '0.75rem',
                }}
              >
                {user.role}
              </span>
              {user.approval_status === 'pending' && (
                <span style={{
                  padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.75rem',
                  background: 'hsl(40 95% 88%)', color: 'hsl(40 80% 30%)',
                }}>⏳ Pending approval</span>
              )}
              {user.approval_status === 'rejected' && (
                <span style={{
                  padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.75rem',
                  background: 'hsl(0 70% 92%)', color: 'hsl(0 70% 35%)',
                }}>⛔ Rejected</span>
              )}
              {user.is_active === false && (
                <span style={{
                  padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.75rem',
                  background: 'hsl(0 0% 90%)', color: 'hsl(0 0% 30%)',
                }}>Inactive</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            data-testid="drawer-close-btn"
            style={{ fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem 0.5rem' }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Inline action row — Message parent (rule c: child-less is allowed for admins) */}
        {user?.role === 'parent' && (
          <div style={{ padding: '0 1.25rem 0.75rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: '0.85rem' }}
              onClick={() => setShowComposer(true)}
              data-testid="drawer-message-btn"
            >
              ✉️ Message {user.full_name ? user.full_name.split(' ')[0] : 'parent'}
            </button>
          </div>
        )}

        <MessageComposer
          open={showComposer}
          onClose={() => setShowComposer(false)}
          user={currentUser}
          parentUserId={user?._id}
          parentName={user?.full_name || user?.email}
          onSent={() => { /* notification fanned out by backend */ }}
        />

        {/* The original drawer body opens below — we just inserted the
            action row above it. */}

        {/* ---------- Tab strip ---------- */}
        <div
          role="tablist"
          style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', flexWrap: 'wrap' }}
        >
          {[
            { id: 'profile', label: '👤 Profile' },
            { id: 'children', label: `👶 Children${children ? ` (${children.length})` : ''}` },
            { id: 'enrollments', label: `📝 Enrolments${enrollments ? ` (${enrollments.length})` : ''}` },
            { id: 'bookings', label: `📅 Bookings${bookings ? ` (${bookings.length})` : ''}` },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              data-testid={`drawer-tab-${t.id}`}
              className={`btn ${tab === t.id ? 'btn-primary' : 'btn-ghost'}`}
              style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div
            style={{
              padding: '0.5rem 0.75rem', background: 'hsl(0 70% 95%)',
              border: '1px solid hsl(0 70% 80%)', borderRadius: 6,
              color: 'hsl(0 70% 35%)', fontSize: '0.85rem', marginBottom: '0.75rem',
            }}
          >{error}</div>
        )}

        {/* ---------- PROFILE ---------- */}
        {tab === 'profile' && (
          <div data-testid="drawer-profile">
            {/* Profile photo — managers can upload on behalf of staff and the
                photo is auto-approved (the upload itself counts as the
                verification step). For non-managerial viewers we still
                show the photo (read-only) so anyone with drawer access
                gets a visual identifier. */}
            <div
              style={{
                display: 'flex', gap: '0.85rem', alignItems: 'center',
                marginBottom: '1rem',
              }}
              data-testid="drawer-profile-photo-block"
            >
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                overflow: 'hidden',
                background: 'hsl(var(--muted))',
                color: 'hsl(var(--muted-foreground))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '1.5rem',
                flexShrink: 0,
                border: '2px solid hsl(var(--border))',
              }}>
                {liveAvatarUrl ? (
                  <img
                    src={liveAvatarUrl}
                    alt=""
                    data-testid="drawer-profile-photo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  (user.full_name || user.email || '?').charAt(0).toUpperCase()
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {user.full_name || user.email}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                  {liveAvatarUrl
                    ? 'Profile photo verified.'
                    : 'No profile photo on file.'}
                </div>
                {canManageThisPhoto && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={photoBusy}
                      data-testid="drawer-upload-photo-btn"
                      style={{ fontSize: '0.8rem' }}
                    >
                      {photoBusy
                        ? 'Uploading…'
                        : (liveAvatarUrl ? '🔁 Replace photo' : '📷 Upload photo')}
                    </button>
                    <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', alignSelf: 'center' }}>
                      Auto-approved — verifies identity for the ID card.
                    </span>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                      onChange={handleManagerPhotoSelected}
                      data-testid="drawer-upload-photo-input"
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </div>
            </div>

            <FieldRow label="Full name" value={user.full_name} />
            <FieldRow label="Email" value={user.email} />
            <FieldRow label="Phone" value={user.phone} />
            <FieldRow label="Role" value={user.role} />
            <FieldRow label="Active" value={user.is_active === false ? 'No' : 'Yes'} />
            <FieldRow label="Approval status" value={user.approval_status || 'n/a'} />
            <FieldRow label="Center" value={user.center_id || '—'} />
            <FieldRow label="Created" value={fmtDate(user.created_at)} />
          </div>
        )}

        {/* ---------- CHILDREN ---------- */}
        {tab === 'children' && (
          <div data-testid="drawer-children">
            {loading && children === null ? (
              <Skeleton />
            ) : !children || children.length === 0 ? (
              <EmptyState
                icon="👶"
                title="No children linked to this user"
                hint={user.role === 'parent'
                  ? 'This parent has not added any children yet.'
                  : 'Children only appear here when this user is a parent.'}
              />
            ) : (
              children.map((c) => (
                <div
                  key={c._id}
                  data-testid={`drawer-child-${c._id}`}
                  style={{
                    display: 'flex', gap: '0.75rem', alignItems: 'center',
                    padding: '0.75rem', marginBottom: '0.5rem',
                    border: '1px solid hsl(var(--border))', borderRadius: 8,
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'hsl(var(--muted))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0,
                  }}>
                    {c.photo_url ? (
                      <img src={c.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: '1.2rem' }}>👶</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {(c.first_name || '') + ' ' + (c.last_name || '')}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                      {c.date_of_birth ? `DOB: ${fmtDate(c.date_of_birth)}` : ''}
                      {c.room_id ? `  •  Room: ${c.room_id}` : ''}
                    </div>
                    {(c.special_needs?.has_sen || (c.dietary_requirements?.allergies?.length)) && (
                      <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                        {c.special_needs?.has_sen && (
                          <span style={{
                            fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: 4,
                            background: 'hsl(280 60% 92%)', color: 'hsl(280 50% 35%)',
                          }}>SEND</span>
                        )}
                        {c.dietary_requirements?.allergies?.length > 0 && (
                          <span style={{
                            fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: 4,
                            background: 'hsl(20 80% 92%)', color: 'hsl(20 70% 35%)',
                          }}>Allergies</span>
                        )}
                      </div>
                    )}
                  </div>
                  <a
                    href={`/child/${c._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost"
                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                    data-testid={`drawer-child-open-${c._id}`}
                  >
                    Open ↗
                  </a>
                </div>
              ))
            )}
          </div>
        )}

        {/* ---------- ENROLMENTS ---------- */}
        {tab === 'enrollments' && (
          <div data-testid="drawer-enrollments">
            {loading && enrollments === null ? (
              <Skeleton />
            ) : !enrollments || enrollments.length === 0 ? (
              <EmptyState
                icon="📝"
                title="No enrolments on file"
                hint="When this parent submits the enrolment wizard, the signed record will appear here."
              />
            ) : (
              enrollments.map((e) => (
                <div
                  key={e._id}
                  data-testid={`drawer-enrollment-${e._id}`}
                  style={{
                    padding: '0.75rem', marginBottom: '0.5rem',
                    border: '1px solid hsl(var(--border))', borderRadius: 8,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {e.child_first_name ? `${e.child_first_name} ${e.child_last_name || ''}` : (e._id || '').slice(0, 8)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                        Status: <strong>{e.status || 'draft'}</strong>
                        {e.submitted_at ? `  •  Submitted ${fmtDate(e.submitted_at)}` : ''}
                        {e.approved_at ? `  •  Approved ${fmtDate(e.approved_at)}` : ''}
                      </div>
                    </div>
                    {e.status === 'submitted' || e.status === 'approved' ? (
                      <a
                        href={`/api/enrollments/${e._id}/printable`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost"
                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                        data-testid={`drawer-enrollment-pdf-${e._id}`}
                      >
                        📄 Open
                      </a>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ---------- BOOKINGS ---------- */}
        {tab === 'bookings' && (
          <div data-testid="drawer-bookings">
            {loading && bookings === null ? (
              <Skeleton />
            ) : !bookings || bookings.length === 0 ? (
              <EmptyState icon="📅" title="No bookings yet" hint="When this family books a session it will appear here." />
            ) : (
              <table style={{ width: '100%', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                    <th style={{ textAlign: 'left', padding: '0.4rem' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem' }}>Session</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b._id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                      <td style={{ padding: '0.4rem' }}>{fmtDate(b.date)}</td>
                      <td style={{ padding: '0.4rem' }}>{b.session_id || b.session_name || '—'}</td>
                      <td style={{ padding: '0.4rem' }}>{b.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const FieldRow = ({ label, value }) => (
  <div style={{ display: 'flex', padding: '0.4rem 0', borderBottom: '1px solid hsl(var(--border))' }}>
    <div style={{ width: 140, color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>{label}</div>
    <div style={{ flex: 1, fontSize: '0.9rem', wordBreak: 'break-word' }}>{value || '—'}</div>
  </div>
);

const Skeleton = () => (
  <div style={{ padding: '1rem' }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{
        height: 56, marginBottom: 8, borderRadius: 8,
        background: 'linear-gradient(90deg, hsl(var(--muted)), hsl(var(--border)), hsl(var(--muted)))',
        backgroundSize: '200% 100%', animation: 'pulse 1.4s ease-in-out infinite',
      }} />
    ))}
  </div>
);

const EmptyState = ({ icon, title, hint }) => (
  <div style={{
    textAlign: 'center', padding: '2rem 1rem',
    color: 'hsl(var(--muted-foreground))',
  }}>
    <div style={{ fontSize: '2rem' }}>{icon}</div>
    <div style={{ fontWeight: 600, marginTop: '0.5rem' }}>{title}</div>
    {hint && <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>{hint}</div>}
  </div>
);

export default UserDetailDrawer;
